// src/features/Transactions/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

// Import the category options defined in filter.tsx
import { categoryOptions } from "./filter";

/**
 * Zod schema for the {t("transactions.new_transaction")} form.
 * - `date` and `strategy_id` are required per the original Ruby form / Pydantic model.
 * - All other fields are optional.
 * - AutocompleteAsync for strategy: both name and id.
 */
const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  category: z.string().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().optional().nullable(),
  quantity: z.coerce.number().optional().nullable(),
  strategy_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_id: z.coerce
    .number({ message: "Strategy is required" })
    .min(1, "Strategy is required"),
});

export function TransactionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Transactions",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Strategy (async autocomplete) */}
                  <AutocompleteAsyncField
                    name="strategy_name"
                    label={t(`${table_name}.columns.strategy_name`, {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    base_url="legacy-quantilia/strategies"
                    column_name="name"
                    id_column="strategy_id"
                    control={form.control}
                    isRequired
                  />

                  {/* Date */}
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                    isRequired
                  />

                  {/* Category */}
                  <SelectField
                    name="category"
                    label={t(`${table_name}.columns.category`, {
                      ns: "tables",
                      defaultValue: "Category",
                    })}
                    options={categoryOptions}
                    control={form.control}
                    allow_empty_option
                  />

                  {/* Quantity */}
                  <InputField
                    name="quantity"
                    label={t(`${table_name}.columns.quantity`, {
                      ns: "tables",
                      defaultValue: "Quantity",
                    })}
                    type="number"
                    control={form.control}
                  />

                  {/* Market Value PTF CCY */}
                  <InputField
                    name="market_value_ptf_ccy"
                    label={t(`${table_name}.columns.market_value_ptf_ccy`, {
                      ns: "tables",
                      defaultValue: "Market Value PTF CCY",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["strategy_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
